import { SCENE_WIDTH } from "Data/Constants";
import Lantern from "GameObjects/StoryGame/ch9/Lantern";
import { SceneSpine } from "GameObjects/StoryGame/SceneSpine";
import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Bounce, Circ, Elastic, Expo, Linear, RoughEase, Sine, TimelineLite, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { lerp } from "html-living-framework/lib/Utils/MathUtils";
import { floatBetween } from "html-living-framework/lib/Utils/Random";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import { list } from "Utils/Reflection";

import { ch9_p05 } from "../../../layouts/ch9_p05/ch9_p05";

export class Chapter9Page5 extends AbstractStoryState implements ReturnType<typeof ch9_p05> {

    readonly scrollContainer = this.addChild(new PIXI.Container());

    readonly bg = [
        this.scrollContainer.addChild(new SceneSprite()),
        this.scrollContainer.addChild(new SceneSprite()),
    ];
    readonly lanternBackContainer = this.scrollContainer.addChild(new PIXI.Container());
    readonly lanternBack = list(()=>this.lanternBackContainer.addChild(new Lantern()));
    readonly holly = [
        this.scrollContainer.addChild(new SceneSprite()),
        this.scrollContainer.addChild(new SceneSprite()),
        this.scrollContainer.addChild(new SceneSprite()),
    ];
    readonly bushes = this.scrollContainer.addChild(new SceneSprite());
    readonly lanternFrontContainer = this.scrollContainer.addChild(new PIXI.Container());
    readonly lanternFront = list(()=>this.lanternFrontContainer.addChild(new Lantern()));

    protected scrollPercent: number = 0;

    public init(pSize: PIXI.Point, pData: any) {
        // exported layouts from Animate CC
        ch9_p05.call(this);

        this.holly[0].visible = true;
        this.holly[1].visible = false;
        this.holly[2].visible = false;

        // super.init calls onResize
        super.init(pSize, pData);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.scroll(this.scrollPercent);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.scroll(this.scrollPercent);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);

        this.lanternFront.forEach(l=>{
            const newTimeline = new TimelineMax();
            newTimeline.repeat(-1);

            newTimeline.to(
                l.onSprite,
                floatBetween(3, 5),
                {
                    pixi: {alpha: 0.5},
                    ease: new RoughEase({
                        taper: 'none',
                        points: 15,
                        strength: 1,
                        randomize: true,
                    }),
                },
            )
            .to(
                l.onSprite,
                floatBetween(3, 5),
                {
                    pixi: {alpha: 1},
                    ease: new RoughEase({
                        taper: 'none',
                        points: 100,
                        strength: 1,
                        randomize: true,
                    }),
                },
            );
        });

        this.lanternBack.forEach(l=>{
            const newTimeline = new TimelineMax();
            newTimeline.repeat(-1);

            newTimeline.to(
                l.onSprite,
                floatBetween(3, 5),
                {
                    pixi: {alpha: 0.5},
                    ease: new RoughEase({
                        taper: 'none',
                        points: 100,
                        strength: 1,
                        randomize: true,
                    }),
                },
            )
            .to(
                l.onSprite,
                floatBetween(3, 5),
                {
                    pixi: {alpha: 1},
                    ease: new RoughEase({
                        taper: 'none',
                        points: 15,
                        strength: 1,
                        randomize: true,
                    }),
                },
            );
        });
    }

    public animateOut(pOnComplete: () => void) {
        super.animateOut(pOnComplete);
    }

    public onAnimateInComplete() {
        super.onAnimateInComplete();

        this.interactive = true;
        this.buttonMode = true;

        const onClick1 = ()=>{
            this.interactive = false;
            this.buttonMode = false;
            this.removeListener(HLF.Input.Events.POINTER_TAP, onClick1);

            this.holly[0].FadeOut();
            this.holly[1].FadeIn();

            new TimelineLite()
                .to(this, 3, {scrollPercent: 0.5})
                .call(()=>{
                    this.interactive = true;
                    this.buttonMode = true;
                    this.addListener(HLF.Input.Events.POINTER_TAP, onClick2);
                });
        }

        const onClick2 = ()=>{
            this.interactive = false;
            this.buttonMode = false;
            this.removeListener(HLF.Input.Events.POINTER_TAP, onClick2);

            this.holly[1].FadeOut();
            this.holly[2].FadeIn();

            new TimelineLite()
                .to(this, 3, {scrollPercent: 1})
                .call(()=>{
                    this.gotoNextState({transition: "left"});
                }, undefined, undefined, "+=1.5");
        }

        this.addListener(HLF.Input.Events.POINTER_TAP, onClick1);
    }

    protected scroll(pPercent: number) {
        const widthPercent = (this._size.x - 1024) / (1664 - 1024);
        const startPos = lerp((1664 - this._size.x - 135)/2, (1664 - this._size.x)/2, widthPercent);
        const endPos = -(this.scrollContainer.width - 1664);

		this.scrollContainer.position.x = lerp(startPos, endPos, pPercent);
    }
}